/** @jsx CanvaSX */
import { omit } from 'lodash';
import { CanvaSX } from '../canvasx';
import {
  DEFAULT_FONT_SIZE,
  applyFontStyles,
  fontForProps,
  measureText,
  parseFont,
  renderText,
  truncateText,
} from '../textRendering';
import { calculateDimensionsForNode, drawRect } from '../util';

type TextElement = CanvaSXElement<TextProps>;

function generateWrappedTextKey(key: string | undefined): string | undefined {
  if (typeof key !== 'string') {
    return;
  }

  let match: ReturnType<RegExp['exec']>;
  if (
    key.startsWith('WrappedText_') &&
    (match = /WrappedText_(\d+):(.*)/.exec(key))
  ) {
    const counter = parseInt(match[1], 10);
    return `WrappedText_${counter + 1}:${match[2]}`;
  } else {
    return `WrappedText_0:${key}`;
  }
}

/**
 * For the given <Text> element, determines if we can break the text to a certain width. If so,
 * it returns an array of 2 strings: the first string will be <= width, the second will be the
 * remaining text. Otherwise it returns `undefined`.
 */
function breakWordsAtWidth(
  ctx: CanvasRenderingContext2D,
  element: TextElement,
  width: number
): [string, string] | undefined {
  const text: string[] = [];
  const children = element.props.children as CanvaSXNode[];

  // We assome all children of `<Text>` are scalar values.
  const words = children.map(String).join('').split(/\s+/);

  ctx.save();
  applyFontStyles(ctx, element.props);

  // Find as many words as we can fit before we hit width
  while (words.length) {
    const textMetrics = measureText(ctx, text.join(' ') + ' ' + words[0]);

    if (textMetrics.width < width) {
      text.push(words.shift()!);
    } else {
      break;
    }
  }

  ctx.restore();

  if (text.length) {
    return [text.join(' '), words.join(' ')];
  }
}

/**
 * Used to split a <Text> element into two separate elements at a specified width. It will return an array
 * of either one or two elements. If it returns one element, it was not split. If it was split, it returns
 * two elements.
 */
export function splitTextElement(
  ctx: CanvasRenderingContext2D,
  element: TextElement,
  width: number
): [TextElement, TextElement] | undefined {
  const wrappedText = breakWordsAtWidth(ctx, element, width);
  if (!wrappedText) {
    return;
  }

  const [text, remainingText] = wrappedText;
  const { key } = element.props;

  const textProps = omit(element.props, [
    'children',
    'key',
    'width',
    'height',
    // Strip out _dimensions to force recalculation of the new <Text> elements
    '_dimensions',
  ]);

  // Split into another text node
  const hoveredKey = element.props.hoveredKey || key;
  const firstElement = (
    <Text {...textProps} key={key} hoveredKey={hoveredKey}>
      {text}
    </Text>
  );
  const wrappedElement = (
    <Text
      {...textProps}
      key={generateWrappedTextKey(key)}
      hoveredKey={hoveredKey}
    >
      {remainingText}
    </Text>
  );

  calculateDimensionsForNode(firstElement);
  calculateDimensionsForNode(wrappedElement);

  return [firstElement, wrappedElement];
}

function normalizeTextChildren(
  children?: CanvaSXIntrinsicProps['children']
): string {
  let normalized: CanvaSXNode[];

  if (Array.isArray(children)) {
    normalized = children;
  } else if (children !== undefined && children !== null) {
    normalized = [children];
  } else {
    normalized = [];
  }

  return normalized.map(String).join('');
}

export type TextProps = {
  color?: CanvasFillStrokeStyles['fillStyle'];
  lineHeight?: number;
  maxWidth?: number;
  opacity?: number;
  overflow?: string;
  _originalWidth?: number;
} & Partial<FontStruct & DimensionStruct & PositionStruct>;

export const Text: CanvaSXComponent<TextProps> = function (props) {
  let {
    children,
    lineHeight = 1,
    maxWidth,
    opacity = 1,
    overflow,
    _originalWidth,
  } = props;
  const { ctx } = CanvaSX.context;

  if (
    Array.isArray(children) &&
    !children.every(
      child =>
        child === null ||
        ['string', 'number', 'undefined'].includes(typeof child)
    )
  ) {
    throw new Error(
      '<Text> currently only supports string or numbers as children.'
    );
  }

  // The width/height of drawRect is only used for border/fill, which we aren't using
  const margin = 0;
  const padding = 0;
  drawRect(ctx, { ...props, width: 0, height: 0, margin, padding }, () => {
    // Calculate vertical offset for rendering text based on the line height
    const font = fontForProps(props);
    const { fontSize } = parseFont(font);
    const verticalOffset = (fontSize * lineHeight - fontSize) / 2;

    ctx.save();
    ctx.translate(0, verticalOffset);
    ctx.globalAlpha = opacity;

    applyFontStyles(ctx, props);

    const text = normalizeTextChildren(children);

    let usedDimensions: DimensionStruct = {
      width: props.width as number,
      height: props.height as number,
    };

    if (typeof _originalWidth !== 'number') {
      // If the parent didn't measure us, we might not have a measured width
      usedDimensions = measureText(ctx, text);
      _originalWidth = usedDimensions.width;
    }

    if (
      maxWidth &&
      _originalWidth &&
      maxWidth > 0 &&
      _originalWidth > maxWidth
    ) {
      usedDimensions.width = maxWidth;

      truncateText(
        ctx,
        text,
        usedDimensions.width,
        props.size || DEFAULT_FONT_SIZE,
        overflow
      );
    } else {
      renderText(ctx, text);
    }

    props._dimensions ||= usedDimensions;

    ctx.restore();
  });

  return null;
};

Text.calculateDimensions = props => {
  // Need to set font for measuring
  const { ctx } = CanvaSX.context;

  ctx.save();
  applyFontStyles(ctx, props);

  const { lineHeight = 1 } = props;
  if (!props._dimensions) {
    const text = normalizeTextChildren(props.children);
    const textDimensions = measureText(ctx, text);
    const dimensions = {
      width: textDimensions.width,
      height: textDimensions.height * lineHeight,
    };

    if (
      typeof props.maxWidth === 'number' &&
      dimensions.width > props.maxWidth
    ) {
      dimensions.width = props.maxWidth;
      props.width = props.maxWidth;
    }

    props._dimensions = dimensions;
  }

  ctx.restore();

  return props._dimensions;
};
